import React from 'react';
import { Box, BoxProps } from '@chakra-ui/react';
import { useDrop } from 'react-dnd';
import { ContentBoxItem, ItemTypes } from '../dnd/types';
import { colors } from '../../../../../../App/ChakraTheme';
import ContentBox, { ContentBoxPreview } from '../dnd/ContentDrag';

interface SlotProps extends BoxProps {
  id: string;
  type?: 'text' | 'image' | 'audio';
  value?: string;
  fontSize?: string;
  updateValue: (
    toKey: string,
    newValue: string,
    fromKey: string,
    type: 'text' | 'image' | 'audio',
  ) => void;
  placeholder?: any;
  slot?: string;
  isDisabled?: boolean;
  onKeyboardDrop?: () => void;
  hasKeyboardSelection?: boolean;
  onRemove?: () => void;
}

const Slot: React.FC<SlotProps> = ({
  id,
  value,
  updateValue,
  slot,
  type,
  placeholder,
  isDisabled,
  fontSize,
  onKeyboardDrop,
  hasKeyboardSelection,
  onRemove,
  ...rest
}) => {
  const [{ highlight }, dropRef] = useDrop(
    () => ({
      accept: ItemTypes.ContentBox,
      drop: (item: ContentBoxItem, monitor) => {
        if (isDisabled) return;
        updateValue(id, item.src, item.id, item.type);
      },
      collect: (monitor) => ({
        highlight: !isDisabled && monitor.isOver(),
      }),
    }),
    [isDisabled, id, updateValue],
  );

  const isKeyboardTarget = hasKeyboardSelection && !isDisabled;

  return (
    <Box
      minW={value || placeholder ? undefined : '128px'}
      minH={value || placeholder ? undefined : '32px'}
      w={value || placeholder ? 'full' : 'fit-content'}
      border={isKeyboardTarget && !value ? '2px dashed' : '1px'}
      borderRadius="8px"
      borderColor={isKeyboardTarget ? 'blue.400' : 'gray.500'}
      color="blue.500"
      background={
        highlight ? colors.applyOpacity(colors.darkblue, 0.35) : 'white'
      }
      display={value || placeholder ? 'flex' : 'inline-flex'}
      position="relative"
      tabIndex={isKeyboardTarget ? 0 : -1}
      role="button"
      aria-label={isKeyboardTarget ? 'Place selected answer here' : undefined}
      _focusVisible={{ outline: '3px solid var(--stemi-focus-color)', outlineOffset: '3px' }}
      onKeyDown={isKeyboardTarget && onKeyboardDrop ? (e: React.KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onKeyboardDrop();
        }
      } : undefined}
      {...rest}
      ref={dropRef}
    >
      {value && type ? (
        <>
          <ContentBox
            id={slot}
            background={
              highlight ? colors.applyOpacity(colors.darkblue, 0.35) : 'white'
            }
            type={type}
            src={value}
            allowWrap
            w="100%"
            maxW="100%"
            fontSize={fontSize}
            textAlign="center"
            align="center"
          />
          {!isDisabled && onRemove && (
            <Box
              as="button"
              aria-label="Remove answer"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); onRemove(); }}
              position="absolute"
              top="-8px"
              right="-8px"
              w="18px"
              h="18px"
              borderRadius="full"
              background="gray.400"
              color="white"
              fontSize="11px"
              fontWeight="bold"
              lineHeight="18px"
              textAlign="center"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{ background: 'gray.600' }}
              _focusVisible={{ outline: '2px solid var(--stemi-focus-color)', outlineOffset: '2px' }}
              zIndex={1}
            >
              ×
            </Box>
          )}
        </>
      ) : placeholder ? (
        <ContentBoxPreview
          opacity={0.5}
          type={placeholder.type}
          src={placeholder.src}
          fontSize={fontSize}
          allowWrap
          w="100%"
          maxW="100%"
          textAlign="center"
          align="center"
        />
      ) : null}
    </Box>
  );
};

export { Slot };
export default Slot;
